import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useLang } from "@/i18n";

const GOLD = "#C7A668";
const ORIGIN = "https://dukk.mx";
const AVIATION_IMG = "https://images.unsplash.com/photo-1770511020240-74d5dc0b1570?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxwcml2YXRlJTIwamV0JTIwZGFyayUyMGx1eHVyeXxlbnwwfHx8fDE3ODY0MDI5MDl8MA&ixlib=rb-4.1.0&q=85";
const AUTOS_IMG = "https://images.unsplash.com/photo-1769641241120-f0dd8669b12d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBjYXIlMjBkYXJrJTIwZ2FyYWdlfGVufDB8fHx8MTc4NjQwMjkwOXww&ixlib=rb-4.1.0&q=85";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement("meta"); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute("content", content);
}
function upsertLink(rel, href, hreflang) {
  const sel = hreflang ? `link[rel="${rel}"][hreflang="${hreflang}"]` : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); if (hreflang) el.setAttribute("hreflang", hreflang); document.head.appendChild(el); }
  el.setAttribute("href", href);
}

function DoorHalf({ side, title, subline, to, img, active, dimmed, onEnter, onLeave, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      className="door-half no-underline relative flex-1 overflow-hidden flex items-center justify-center focus:outline-none"
      style={{ minHeight: 0 }}
    >
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `url(${img})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          transform: active ? "scale(1.06)" : "scale(1.0)",
          filter: dimmed ? "saturate(.5) brightness(.55)" : "saturate(1) brightness(1)",
          transition: "transform 1.1s cubic-bezier(.16,.84,.44,1), filter .6s ease",
        }}
      />
      <div
        className="absolute inset-0"
        style={{
          background: active
            ? "linear-gradient(180deg, rgba(10,10,11,.35) 0%, rgba(10,10,11,.55) 100%)"
            : "linear-gradient(180deg, rgba(10,10,11,.62) 0%, rgba(10,10,11,.82) 100%)",
          transition: "background .6s ease",
        }}
      />
      <div className="relative z-10 text-center px-6" style={{ transform: active ? "translateY(-4px)" : "none", transition: "transform .6s ease" }}>
        <div
          className="font-display"
          style={{ fontSize: "clamp(30px,4.4vw,62px)", lineHeight: 1, letterSpacing: ".04em", color: active ? "#FFFFFF" : "#EDEAE3", fontWeight: 300 }}
        >
          {title}
        </div>
        <div className="font-mono" style={{ marginTop: 16, fontSize: "clamp(9.5px,1vw,12px)", letterSpacing: ".22em", color: active ? GOLD : "#9aa0a7", transition: "color .5s ease" }}>
          {subline}
        </div>
        <div
          className="font-mono inline-block"
          style={{
            marginTop: 26, fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase",
            color: active ? "#0a0a0a" : "#EDEAE3",
            background: active ? GOLD : "transparent",
            border: active ? "none" : "1px solid rgba(237,234,227,.35)",
            padding: "10px 22px", borderRadius: 999, transition: "all .4s ease",
          }}
        >
          {side === "aviation" ? "→" : "→"} {title}
        </div>
      </div>
    </Link>
  );
}

export default function TheDoor({ lang: langProp }) {
  const { lang, setLang, L } = useLang();
  const [hover, setHover] = useState(null);

  useEffect(() => { if (langProp) setLang(langProp); }, [langProp, setLang]);

  const en = (langProp || lang) === "en";

  useEffect(() => {
    const title = en ? "Dukk — Care for what flies and what drives" : "Dukk — El cuidado de lo que vuela y de lo que rueda";
    const desc = en
      ? "Dukk: one house, two specialties. Aviation-grade care for private aircraft, and the same standard for luxury cars in Mexico. Choose your world."
      : "Dukk: una casa, dos especialidades. Cuidado grado aeronáutico para aeronaves privadas, y el mismo estándar para autos de lujo en México. Elige tu mundo.";
    const canonical = ORIGIN + (en ? "/en" : "/");
    document.documentElement.lang = en ? "en" : "es-MX";
    document.title = title;
    upsertMeta("name", "description", desc);
    upsertLink("canonical", canonical);
    upsertLink("alternate", `${ORIGIN}/`, "es-MX");
    upsertLink("alternate", `${ORIGIN}/en`, "en");
    upsertLink("alternate", `${ORIGIN}/`, "x-default");
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:site_name", "dukk");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", desc);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", `${ORIGIN}/og-image.png`);
    upsertMeta("property", "og:locale", en ? "en_US" : "es_MX");
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", desc);
  }, [en]);

  const aviationTo = en ? "/en/aviation" : "/aviacion";
  const autosTo = en ? "/en/cars" : "/autos";

  return (
    <motion.div
      data-testid="the-door"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.8 }}
      className="fixed inset-0 flex flex-col md:flex-row"
      style={{ background: "#0A0A0B" }}
    >
      {/* Center brand + tagline (pointer-events none so halves stay hoverable) */}
      <div className="absolute inset-x-0 top-0 z-30 flex flex-col items-center pointer-events-none" style={{ paddingTop: "clamp(28px,5vh,56px)" }}>
        <svg viewBox="0 0 220 50" height="34" fill="none" aria-label="DUKK">
          <g transform="translate(14 9) scale(0.2909)" stroke="#F5F2EE" strokeLinecap="round" fill="none">
            <path d="M5 86C35 72 75 44 118 10" strokeWidth="1.9" />
            <path d="M5 99C35 86 75 58 111 28" strokeWidth="1.9" />
            <path d="M118 10C116 17 113 23 111 28" strokeWidth="1.9" />
            <line x1="0" y1="104" x2="160" y2="104" strokeWidth="1.4" />
          </g>
          <text x="76" y="25" fontFamily="'Cormorant Garamond','Cormorant',Georgia,serif" fontWeight="300" fontSize="28" letterSpacing="8" fill="#F5F2EE">DUKK</text>
        </svg>
        <div className="font-mono" style={{ marginTop: 14, fontSize: "clamp(8.5px,1vw,11px)", letterSpacing: ".2em", color: "#9aa0a7", textAlign: "center", padding: "0 16px" }}>
          {en ? "CARE FOR WHAT FLIES · AND WHAT DRIVES" : "EL CUIDADO DE LO QUE VUELA · Y DE LO QUE RUEDA"}
        </div>
      </div>

      <DoorHalf
        side="aviation" testid="door-aviation" to={aviationTo}
        title={en ? "AVIATION" : "AVIACIÓN"}
        subline={en ? "AVIATION-GRADE · TO YOUR HANGAR" : "GRADO AERONÁUTICO · A TU HANGAR"}
        img={AVIATION_IMG}
        active={hover === "aviation"} dimmed={hover === "autos"}
        onEnter={() => setHover("aviation")} onLeave={() => setHover(null)}
      />
      <DoorHalf
        side="autos" testid="door-autos" to={autosTo}
        title={en ? "AUTOMOBILES" : "AUTOMÓVILES"}
        subline={en ? "THE SAME STANDARD · TO YOUR GARAGE" : "EL MISMO ESTÁNDAR · A TU GARAJE"}
        img={AUTOS_IMG}
        active={hover === "autos"} dimmed={hover === "aviation"}
        onEnter={() => setHover("autos")} onLeave={() => setHover(null)}
      />

      {/* Bottom line + language */}
      <div className="absolute inset-x-0 bottom-0 z-30 flex flex-col items-center gap-3 pointer-events-none" style={{ paddingBottom: "clamp(22px,4vh,40px)" }}>
        <div className="font-mono" style={{ fontSize: "clamp(8.5px,1vw,11px)", letterSpacing: ".18em", color: "#7a808a", textAlign: "center", padding: "0 16px" }}>
          {en ? "One house. Two specialties. Choose your world." : "Una casa. Dos especialidades. Elige tu mundo."}
        </div>
        <Link
          to={en ? "/" : "/en"}
          data-testid="door-lang-toggle"
          className="font-mono no-underline pointer-events-auto"
          style={{ fontSize: 10.5, letterSpacing: ".1em", color: "#EDEAE3", border: "1px solid rgba(237,234,227,.25)", borderRadius: 4, padding: "5px 12px" }}
        >
          {en ? "ES" : "EN"}
        </Link>
      </div>
    </motion.div>
  );
}
